import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  InputNumber,
  Statistic,
  Divider,
  Typography,
  Tag,
  Select,
} from 'antd'
import { useMasterData } from '@/context/MasterDataContext'
import {
  ThunderboltOutlined,
  CalculatorOutlined,
  ArrowRightOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  LineChartOutlined
} from '@ant-design/icons'

const { Title, Text } = Typography

interface CalculatorInputs {
  tariffId?: string
  peakUnits: number
  normalUnits: number
  offPeakUnits: number
  tnebPeakRate: number
  tnebNormalRate: number
  tnebOffPeakRate: number
  eTaxPercent: number
  sakthiRate: number
}

interface CalculationResult {
  totalUnits: number
  tnebSubtotal: number
  tnebETaxAmount: number
  tnebTotalBill: number
  tnebAvgRate: number
  sakthiTotalBill: number
  monthlySavings: number
  annualSavings: number
  savingsPercent: number
}

const SavingsCalculator: React.FC = () => {
  const [form] = Form.useForm<CalculatorInputs>()
  const [result, setResult] = useState<CalculationResult | null>(null)

  const { tnebTariffs } = useMasterData()
  const activeTnebTariffs = tnebTariffs.filter(t => t.status === 'active')
  const [isInitialized, setIsInitialized] = useState(false)

  // Default values
  useEffect(() => {
    if (activeTnebTariffs.length > 0 && !isInitialized) {
      const defaultTariff = activeTnebTariffs[0]
      form.setFieldsValue({
        tariffId: defaultTariff.id,
        peakUnits: 10000,
        normalUnits: 25000,
        offPeakUnits: 5000,
        tnebPeakRate: defaultTariff.peakRate || defaultTariff.ratePerUnit,
        tnebNormalRate: defaultTariff.ratePerUnit,
        tnebOffPeakRate: defaultTariff.nightC1Rate || defaultTariff.nightRate || defaultTariff.ratePerUnit,
        eTaxPercent: defaultTariff.electricityTax || 0,
        sakthiRate: 6.50
      })
      handleCalculate(form.getFieldsValue())
      setIsInitialized(true)
    } else if (activeTnebTariffs.length === 0 && !isInitialized) {
      form.setFieldsValue({
        peakUnits: 10000,
        normalUnits: 25000,
        offPeakUnits: 5000,
        tnebPeakRate: 8.50,
        tnebNormalRate: 7.50,
        tnebOffPeakRate: 7.10,
        eTaxPercent: 5.0,
        sakthiRate: 6.50
      })
      handleCalculate(form.getFieldsValue())
      setIsInitialized(true)
    }
  }, [form, tnebTariffs, isInitialized])

  const handleTariffChange = (tariffId: string) => {
    const tariff = activeTnebTariffs.find(t => t.id === tariffId)
    if (tariff) {
      form.setFieldsValue({
        tnebPeakRate: tariff.peakRate || tariff.ratePerUnit,
        tnebNormalRate: tariff.ratePerUnit,
        tnebOffPeakRate: tariff.nightC1Rate || tariff.nightRate || tariff.ratePerUnit,
        eTaxPercent: tariff.electricityTax || 0,
      })
      handleCalculate(form.getFieldsValue())
    }
  }

  const handleCalculate = (values: CalculatorInputs) => {
    const {
      peakUnits = 0,
      normalUnits = 0,
      offPeakUnits = 0,
      tnebPeakRate = 0,
      tnebNormalRate = 0,
      tnebOffPeakRate = 0,
      eTaxPercent = 0,
      sakthiRate = 0
    } = values

    const totalUnits = peakUnits + normalUnits + offPeakUnits

    // TNEB Calc
    const peakCost = peakUnits * tnebPeakRate
    const normalCost = normalUnits * tnebNormalRate
    const offPeakCost = offPeakUnits * tnebOffPeakRate
    const tnebSubtotal = peakCost + normalCost + offPeakCost
    const tnebETaxAmount = tnebSubtotal * (eTaxPercent / 100)
    const tnebTotalBill = tnebSubtotal + tnebETaxAmount
    const tnebAvgRate = totalUnits > 0 ? tnebTotalBill / totalUnits : 0

    // Sakthi Calc
    const sakthiTotalBill = totalUnits * sakthiRate

    // Savings
    const monthlySavings = tnebTotalBill - sakthiTotalBill
    const annualSavings = monthlySavings * 12
    const savingsPercent = tnebTotalBill > 0 ? (monthlySavings / tnebTotalBill) * 100 : 0

    setResult({
      totalUnits,
      tnebSubtotal,
      tnebETaxAmount,
      tnebTotalBill,
      tnebAvgRate,
      sakthiTotalBill,
      monthlySavings,
      annualSavings,
      savingsPercent
    })
  }

  const formatCurrency = (val: number) => `₹${Math.round(val).toLocaleString('en-IN')}`

  return (
    <div style={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(0,153,204,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CalculatorOutlined style={{ fontSize: 24, color: '#0099CC' }} />
        </div>
        <div>
          <Title level={2} style={{ margin: 0, color: '#001A4D', letterSpacing: '-0.02em' }}>Slot-based Savings Calculator</Title>
          <Text type="secondary">Calculate precise savings based on Time-of-Day (TOD) consumption and rates.</Text>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* Input Panel */}
        <Col xs={24} lg={10}>
          <Card 
            title={
              <span style={{ fontSize: 16, fontWeight: 700, color: '#001A4D' }}>
                Consumption & Rate Details
              </span>
            }
            bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
          >
            <Form
              form={form}
              layout="vertical"
              onValuesChange={(_, allValues) => handleCalculate(allValues)}
            >
              <div style={{ padding: '12px 16px', background: '#F8FAFC', borderRadius: 8, marginBottom: 20 }}>
                <Text strong style={{ color: '#0F172A', display: 'block', marginBottom: 16 }}>
                  Monthly Consumption (kWh)
                </Text>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="peakUnits" label="Peak Hrs">
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="normalUnits" label="Normal Hrs">
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="offPeakUnits" label="Off-Peak Hrs">
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div style={{ padding: '12px 16px', background: '#FEE2E2', borderRadius: 8, marginBottom: 20, border: '1px solid #FECACA' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Text strong style={{ color: '#991B1B', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <BankOutlined /> TNEB Grid Rates (₹/kWh)
                  </Text>
                  <Form.Item name="tariffId" style={{ marginBottom: 0, width: 250 }}>
                    <Select
                      size="small"
                      placeholder="Select Tariff Category"
                      onChange={handleTariffChange}
                      options={activeTnebTariffs.map(t => ({
                        value: t.id,
                        label: `${t.category} - ${t.description}`
                      }))}
                    />
                  </Form.Item>
                </div>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="tnebPeakRate" label="Peak Rate">
                      <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="tnebNormalRate" label="Normal Rate">
                      <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="tnebOffPeakRate" label="Off-Peak Rate">
                      <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="eTaxPercent" label="Electricity Tax (eTax %)" style={{ marginBottom: 0 }}>
                  <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} />
                </Form.Item>
              </div>

              <div style={{ padding: '12px 16px', background: '#ECFDF5', borderRadius: 8, border: '1px solid #A7F3D0' }}>
                <Text strong style={{ color: '#065F46', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
                  <SafetyCertificateOutlined /> Sakthi Power Proposed Rate
                </Text>
                <Form.Item name="sakthiRate" label="Proposed Flat Rate (₹/kWh)" style={{ marginBottom: 0 }}>
                  <InputNumber style={{ width: '100%' }} min={0} step={0.1} size="large" />
                </Form.Item>
              </div>
            </Form>
          </Card>
        </Col>

        {/* Results Panel */}
        <Col xs={24} lg={14}>
          {result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
              
              {/* TNEB vs Sakthi Comparison */}
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Card 
                    bordered={false} 
                    style={{ borderRadius: 12, borderTop: '4px solid #DC2626', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '100%' }}
                  >
                    <Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>If you stay with TNEB</Text>
                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text type="secondary">Energy Charges</Text>
                        <Text strong>{formatCurrency(result.tnebSubtotal)}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text type="secondary">eTax ({form.getFieldValue('eTaxPercent')}%)</Text>
                        <Text strong>{formatCurrency(result.tnebETaxAmount)}</Text>
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong style={{ fontSize: 16 }}>Total Monthly Bill</Text>
                        <Title level={3} style={{ margin: 0, color: '#DC2626' }}>{formatCurrency(result.tnebTotalBill)}</Title>
                      </div>
                      <div style={{ marginTop: 16, background: '#FEF2F2', padding: 8, borderRadius: 6, textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Effective Avg Rate:</Text>
                        <strong style={{ marginLeft: 6, color: '#991B1B' }}>₹{result.tnebAvgRate.toFixed(2)}/kWh</strong>
                      </div>
                    </div>
                  </Card>
                </Col>

                <Col xs={24} sm={12}>
                  <Card 
                    bordered={false} 
                    style={{ borderRadius: 12, borderTop: '4px solid #059669', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '100%' }}
                  >
                    <Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>Switch to Sakthi Power</Text>
                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text type="secondary">Total Consumption</Text>
                        <Text strong>{result.totalUnits.toLocaleString('en-IN')} kWh</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text type="secondary">Flat Rate</Text>
                        <Text strong>₹{form.getFieldValue('sakthiRate').toFixed(2)}/kWh</Text>
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong style={{ fontSize: 16 }}>Total Monthly Bill</Text>
                        <Title level={3} style={{ margin: 0, color: '#059669' }}>{formatCurrency(result.sakthiTotalBill)}</Title>
                      </div>
                      <div style={{ marginTop: 16, background: '#ECFDF5', padding: 8, borderRadius: 6, textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>No eTax applicable</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* Massive Savings Highlight */}
              <Card 
                bordered={false} 
                style={{ 
                  borderRadius: 12, 
                  background: 'linear-gradient(135deg, #001A4D 0%, #004d99 100%)',
                  color: 'white',
                  boxShadow: '0 8px 24px rgba(0,26,77,0.15)',
                  flex: 1
                }}
                styles={{ body: { display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', padding: '32px' } }}
              >
                <Row align="middle" gutter={[32, 24]}>
                  <Col xs={24} md={14}>
                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 16, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                      Your Estimated Savings
                    </Text>
                    <Title style={{ color: 'white', fontSize: 48, margin: '8px 0', fontWeight: 800 }}>
                      {formatCurrency(result.annualSavings)}
                    </Title>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.9)' }}>Per Year</span>
                      <Tag color="gold" style={{ fontSize: 14, padding: '4px 8px', borderRadius: 6, border: 'none', fontWeight: 700 }}>
                        {result.savingsPercent.toFixed(1)}% Cost Reduction
                      </Tag>
                    </div>
                  </Col>
                  <Col xs={24} md={10}>
                    <div style={{ background: 'rgba(255,255,255,0.1)', padding: '20px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.2)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                        <span style={{ color: 'rgba(255,255,255,0.8)' }}>Monthly Savings</span>
                        <strong style={{ fontSize: 18, color: '#4ADE80' }}>{formatCurrency(result.monthlySavings)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'rgba(255,255,255,0.8)' }}>TNEB Avg vs Sakthi</span>
                        <strong style={{ fontSize: 18, color: '#4ADE80' }}>
                          ₹{result.tnebAvgRate.toFixed(2)} <ArrowRightOutlined style={{ fontSize: 14, margin: '0 4px' }} /> ₹{form.getFieldValue('sakthiRate').toFixed(2)}
                        </strong>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>

            </div>
          )}
        </Col>
      </Row>
    </div>
  )
}

export default SavingsCalculator
